"use client";
import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldLabel,
  FieldError,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea";
import { Info } from "lucide-react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { cn } from "@/lib/utils";
import { useR2Upload } from "../lib/useR2Upload";
import {
  buildLessonMarkdownObjectKey,
  getLessonMarkdownAllowedHosts,
  getLessonMarkdownUploadTarget,
  getLessonMarkdownUploadTargetForPublicUrl,
  withLessonMarkdownCacheBust,
} from "@/lib/r2LessonMarkdownPath";

function isProbablyUrl(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
}

function normalizeR2PublicUrlForPersistence(value) {
  if (!isProbablyUrl(value)) return null;
  try {
    const u = new URL(String(value).trim());
    u.search = "";
    u.hash = "";
    return u.toString();
  } catch {
    return null;
  }
}

const editLessonSchema = z.object({
  title: z.string().min(1, "Lesson title is required"),
  order: z.preprocess((val) => {
    if (typeof val === "string") return Number(val);
    return val;
  }, z.number().min(1, "Order must be >= 1")),
  videoUrl: z.string().url().optional().or(z.literal("")),
  markdownUrl: z.string().url().optional().or(z.literal("")),
  content: z.string().optional(),
});

export function EditLessonDialog({
  lessonId,
  courseSlug = "",
  topicId,
  topicOrder,
  topicTitle,
  initialData = {
    title: "",
    order: 1,
    videoUrl: "",
    markdownUrl: "",
    content: "",
  },
  triggerButtonProps = {},
  onLessonAdded,
  refetchTopics,
}) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [isLoadingMarkdown, setIsLoadingMarkdown] = useState(false);
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const { isUploading, uploadFile } = useR2Upload();

  const didInitRef = useRef(false);
  const initLessonIdRef = useRef(null);
  const initialDataRef = useRef(initialData);
  const activeMarkdownFetchRef = useRef(null);
  const markdownFetchSeqRef = useRef(0);
  const initialMarkdownTextRef = useRef("");

  const objectKeyFromPublicUrlUnsafe = (value) => {
    if (!isProbablyUrl(value)) return null;
    try {
      const u = new URL(String(value).trim());
      const key = u.pathname.replace(/^\/+/, "");
      return key || null;
    } catch {
      return null;
    }
  };

  const objectKeyFromPublicUrl = (value) => {
    if (!isProbablyUrl(value)) return null;
    try {
      const u = new URL(String(value).trim());
      // Only reuse object keys from our known public hosts to avoid writing to
      // unexpected paths when a user pastes a GitHub/raw URL.
      const allowedHosts = new Set(getLessonMarkdownAllowedHosts());
      if (allowedHosts.size > 0 && !allowedHosts.has(u.host)) {
        return null;
      }

      const key = u.pathname.replace(/^\/+/, "");
      return key || null;
    } catch {
      return null;
    }
  };

  const ensureMdExtension = (path) => {
    if (!path) return path;
    return path.toLowerCase().endsWith(".md") ? path : `${path}.md`;
  };

  const getLessonMarkdownUrlFromSnapshot = (snapshot) => {
    if (!snapshot) return "";
    const candidates = [
      snapshot.content,
      snapshot.markdownUrl,
      snapshot.markdown_url,
      snapshot.r2_public_url,
    ];
    for (const candidate of candidates) {
      if (isProbablyUrl(candidate)) return String(candidate).trim();
    }
    return "";
  };
  const fetchMarkdownText = async (url, signal) => {
    const cacheBustedUrl = withLessonMarkdownCacheBust(url);
    const res = await fetch(cacheBustedUrl, { signal, cache: "no-store" });
    if (!res.ok) {
      throw new Error(`Failed to fetch markdown (HTTP ${res.status})`);
    }
    return await res.text();
  };
  const form = useForm({
    resolver: zodResolver(editLessonSchema),
    defaultValues: {
      title: initialData.title || "",
      order: initialData.order || 1,
      videoUrl: initialData.videoUrl || "",
      markdownUrl:
        initialData.markdownUrl ||
        (isProbablyUrl(initialData.content) ? String(initialData.content).trim() : "") ||
        initialData.r2_public_url ||
        "",
      // `content` is markdown text. If the backend stored a URL in `content`,
      // we fetch the text from that URL instead of rendering it as text.
      content: isProbablyUrl(initialData.content) ? "" : (initialData.content || ""),
    },
  });

  // Keep the latest initialData, but don't let it reset the form while the dialog is open.
  useEffect(() => {
    if (!open) {
      initialDataRef.current = initialData;
    }
  }, [initialData, open]);

  // Reset init state and cancel any in-flight markdown load when the dialog closes.
  useEffect(() => {
    if (open) return;
    didInitRef.current = false;
    initLessonIdRef.current = null;
    if (activeMarkdownFetchRef.current) {
      activeMarkdownFetchRef.current.abort();
      activeMarkdownFetchRef.current = null;
    }
    setIsLoadingMarkdown(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // Initialize the form only once per open (or if lessonId changes while open).
    if (didInitRef.current && initLessonIdRef.current === lessonId) {
      return;
    }
    didInitRef.current = true;
    initLessonIdRef.current = lessonId;

    const snapshot = initialDataRef.current || {};
    const savedContentText = snapshot?.content || "";
    const savedMarkdownUrl = getLessonMarkdownUrlFromSnapshot(snapshot);
    const markdownUrlValue =
      savedMarkdownUrl || (isProbablyUrl(savedContentText) ? savedContentText : "");

    form.reset({
      title: snapshot.title || "",
      order: snapshot.order || 1,
      videoUrl: snapshot.videoUrl || "",
      markdownUrl: markdownUrlValue,
      // `content` is markdown text. If missing, we'll fetch from the URL.
      content: isProbablyUrl(savedContentText) ? "" : (savedContentText || ""),
    });

    // Baseline for change detection (not solely RHF dirty state).
    initialMarkdownTextRef.current = isProbablyUrl(savedContentText)
      ? ""
      : (savedContentText || "");

    const urlToFetch = isProbablyUrl(markdownUrlValue)
      ? String(markdownUrlValue).trim()
      : null;

    if (!urlToFetch) return;

    // Cancel any previous markdown load (defensive), then fetch.
    if (activeMarkdownFetchRef.current) {
      activeMarkdownFetchRef.current.abort();
    }
    const controller = new AbortController();
    activeMarkdownFetchRef.current = controller;
    const seq = ++markdownFetchSeqRef.current;

    setIsLoadingMarkdown(true);
    fetchMarkdownText(urlToFetch, controller.signal)
      .then((text) => {
        // Ignore if a newer fetch started.
        if (markdownFetchSeqRef.current !== seq) return;

        // If the user has started editing, never overwrite their input.
        const contentState = form.getFieldState("content");
        if (contentState?.isDirty) return;

        // Do not mark dirty just because we loaded content.
        const normalized = text ?? "";
        form.setValue("content", normalized, { shouldDirty: false });
        initialMarkdownTextRef.current = normalized;
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        console.error("Failed to load markdown content from URL", err);
        toast.error(
          "Could not load the markdown file content (CORS or missing file)."
        );
      })
      .finally(() => {
        if (markdownFetchSeqRef.current !== seq) return;
        setIsLoadingMarkdown(false);
      });
  }, [open, lessonId, form]);

  const watchedMarkdownUrl = form.watch("markdownUrl");
  useEffect(() => {
    if (!open) return;

    const url = typeof watchedMarkdownUrl === "string" ? watchedMarkdownUrl.trim() : "";
    if (!isProbablyUrl(url)) return;

    const contentState = form.getFieldState("content");
    if (contentState?.isDirty) return;

    if (activeMarkdownFetchRef.current) {
      activeMarkdownFetchRef.current.abort();
    }
    const controller = new AbortController();
    activeMarkdownFetchRef.current = controller;
    const seq = ++markdownFetchSeqRef.current;

    setIsLoadingMarkdown(true);
    fetchMarkdownText(url, controller.signal)
      .then((text) => {
        if (markdownFetchSeqRef.current !== seq) return;
        const state = form.getFieldState("content");
        if (state?.isDirty) return;
        const normalized = text ?? "";
        form.setValue("content", normalized, { shouldDirty: false });
        initialMarkdownTextRef.current = normalized;
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        console.error("Failed to load markdown content from URL", err);
        toast.error(
          "Could not load the markdown file content (CORS or missing file)."
        );
      })
      .finally(() => {
        if (markdownFetchSeqRef.current !== seq) return;
        setIsLoadingMarkdown(false);
      });
  }, [open, watchedMarkdownUrl, form]);

  // Markdown editor handlers
  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownSave = (value) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log("[lesson-markdown] editor->form", {
        lessonId,
        length: typeof value === "string" ? value.length : null,
        head: typeof value === "string" ? value.slice(0, 80) : null,
      });
    }
    form.setValue("content", value, { shouldDirty: true, shouldValidate: true });
    setMarkdownEditorOpen(false);
  };

  const handleOpenMarkdownEditor = () => {
    setMarkdownEditorOpen(true);
  };

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const token = await getToken();
      if (!token) return;

      if (!lessonId) {
        toast.error("Lesson ID is required for editing");
        setIsSubmitting(false);
        return;
      }

      const providedMarkdownUrl = values.markdownUrl?.trim() || "";
      const contentState = form.getFieldState("content");
      const contentDirty = Boolean(contentState?.isDirty);

      let markdownText = typeof values.content === "string" ? values.content : "";
      let trimmedMarkdownText = markdownText.trim();

      const baselineText =
        typeof initialMarkdownTextRef.current === "string"
          ? initialMarkdownTextRef.current
          : "";
      const contentChanged = markdownText !== baselineText;

      const providedStableUrl = providedMarkdownUrl
        ? normalizeR2PublicUrlForPersistence(providedMarkdownUrl)
        : null;

      const initialSnapshot = initialDataRef.current || {};
      const initialSavedUrl =
        initialSnapshot?.r2_public_url || initialSnapshot?.markdownUrl || "";
      const initialStableUrl = initialSavedUrl
        ? normalizeR2PublicUrlForPersistence(initialSavedUrl)
        : null;
      const canTrustProvidedUrl =
        Boolean(providedStableUrl) &&
        Boolean(initialStableUrl) &&
        providedStableUrl === initialStableUrl;

      let r2PublicUrl = providedStableUrl || null;
      const keyFromUrl = r2PublicUrl
        ? objectKeyFromPublicUrl(r2PublicUrl) ||
          (canTrustProvidedUrl ? objectKeyFromPublicUrlUnsafe(r2PublicUrl) : null)
        : null;
      let r2ObjectKey = keyFromUrl;

      if (trimmedMarkdownText) {
        const shouldUpload = !r2PublicUrl || contentDirty || contentChanged;
        if (shouldUpload) {
          const fromUrl = keyFromUrl;

          // IMPORTANT: For edits, always overwrite the existing R2 object key
          // if the saved URL belongs to our allowed public hosts.
          const computedKey =
            !fromUrl && courseSlug
              ? buildLessonMarkdownObjectKey({
                  courseSlug,
                  topicOrder,
                  topicTitle,
                  topicId,
                  lessonOrder: values.order,
                  lessonTitle: values.title,
                })
              : null;

          const fallbackKey = `lessons/lesson-${lessonId}`;
          const objectKey = ensureMdExtension(fromUrl || computedKey || fallbackKey);
          const fileName =
            objectKey.split("/").pop() || `lesson-${lessonId}.md`;

          const markdownFile = new File([markdownText], fileName, {
            type: "text/markdown",
          });

          const uploadTargetFromExistingUrl = r2PublicUrl
            ? getLessonMarkdownUploadTargetForPublicUrl(r2PublicUrl)
            : null;
          const { bucketName, publicUrlBase } =
            uploadTargetFromExistingUrl || getLessonMarkdownUploadTarget();

          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log("[lesson-markdown] put", {
              lessonId,
              contentDirty,
              contentChanged,
              hasUrl: Boolean(r2PublicUrl),
              objectKey,
              bucketName,
              publicUrlBase,
            });
          }

          const uploadedUrl = await uploadFile(markdownFile, objectKey, {
            silent: true,
            bucketName,
            publicUrlBase,
            cacheControl: "no-cache, max-age=0, must-revalidate",
          });
          if (!uploadedUrl) {
            toast.error("Failed to upload the edited markdown to storage.");
            return;
          }
          const stableUploadedUrl =
            normalizeR2PublicUrlForPersistence(uploadedUrl) || uploadedUrl;
          r2PublicUrl = stableUploadedUrl;
          r2ObjectKey = objectKey;
        }
      }

      const payload = {
        title: values.title,
        order: Number(values.order),
        video_url: values.videoUrl || null,
        // Send multiple key variants for backend compatibility.
        r2_object_key: r2ObjectKey,
        r2_public_url: r2PublicUrl,
        markdown_url: r2PublicUrl,
        markdownUrl: r2PublicUrl,
        // Backend expects the markdown URL here (not the markdown text).
        content: r2PublicUrl || null,
      };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const editLessonResponse = await axios.put(
        api_endpoint + `/lesson/update/${lessonId}`,
        payload,
        reqConfig
      );
      const lessonData =
        editLessonResponse.data?.lesson ?? editLessonResponse.data;
      console.log("Updated Lesson: ", lessonData);
      toast.success(`Updated lesson "${lessonData.title}" successfully!`);
      setOpen(false);
      form.reset({
        title: lessonData.title,
        order: lessonData.order,
        videoUrl: lessonData.video_url || "",
        markdownUrl: r2PublicUrl || getLessonMarkdownUrlFromSnapshot(lessonData) || "",
        // Keep the editor showing markdown text (not the persisted URL).
        content: markdownText || "",
      });
      initialMarkdownTextRef.current = markdownText || "";
      if (onLessonAdded) onLessonAdded(lessonData);
      if (refetchTopics) refetchTopics();
    } catch (error) {
      console.error("Error updating lesson:", error);
      toast.error("Failed to update lesson. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset to initial values instead
    form.reset({
      title: initialData.title || "",
      order: initialData.order || 1,
      videoUrl: initialData.videoUrl || "",
      markdownUrl:
        initialData.markdownUrl ||
        (isProbablyUrl(initialData.content) ? String(initialData.content).trim() : "") ||
        initialData.r2_public_url ||
        "",
      content: isProbablyUrl(initialData.content) ? "" : (initialData.content || ""),
    });
    setOpen(false);
  };


  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-(--neutral-300) text-(--neutral-900) bg-(--surface-panel) hover:bg-(--neutral-100)"
          {...triggerButtonProps}
        >
          {/* <Edit className="w-4 h-4 mr-2" /> */}
          Edit Lesson
        </Button>
      </DialogTrigger>
      <DialogContent className="h-auto sm:h-auto bg-(--surface-panel) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">Edit Lesson</DialogTitle>
          <DialogDescription className="font-body text-(--neutral-600)">
            Update the details of this lesson.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6"
              id="edit-lesson-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Lesson Title *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          placeholder="Enter lesson title"
                          className={cn(
                            "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
                <FormField
                  control={form.control}
                  name="order"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Order *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          type="number"
                          min="1"
                          className={cn(
                            "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="videoUrl"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Video URL
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-heading">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="https://youtube.com/..."
                        type="url"
                        className={cn(
                          "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="markdownUrl"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Markdown File URL
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-heading">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="https://randomurl.md/... or any r2 markdown file URL"
                        type="url"
                        className={cn(
                          "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field, fieldState }) => (
                  <Field>
                    <div className="flex items-center justify-between gap-3">
                      <FieldLabel>
                        Content
                        <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-heading">
                          <Info className="w-3 h-3" />
                          Markdown supported
                        </div>
                      </FieldLabel>
                      <Button
                        type="button"
                        onClick={handleOpenMarkdownEditor}
                        size=""
                        variant="outline"
                        className="font-body"
                      >
                        Edit in Fullscreen
                      </Button>
                    </div>
                    <FieldContent>
                      <Textarea
                        {...field}
                        rows={8}
                        placeholder="Enter lesson content using Markdown"
                        disabled={isLoadingMarkdown || isSubmitting || isUploading}
                        className={cn(
                          "min-h-50 font-mono text-sm bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500)",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError
                        errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                      />
                    </FieldContent>
                  </Field>
                )}
              />
            </form>
          </Form>
        </div>

        <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className=""
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant=""
              form="edit-lesson-form"
              className=""
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center w-full">
                  <ClipLoader size={20} color="var(--neutral-50)" />
                </span>
              ) : (
                "Save Changes"
              )}
            </Button>
        </DialogFooter>
        {/* Markdown Editor Dialog - positioned outside DialogContent but inside Dialog */}
        <MarkdownEditorDialog
          open={markdownEditorOpen}
          initialValue={form.watch("content") || ""}
          onClose={handleMarkdownEditorClose}
          onSave={handleMarkdownSave}
        />
      </DialogContent>
    </Dialog>
  );
}
